// Phase T2.0A Prompt 2 hotfix — Local flyer picker for CREATE flows.
//
// Different from EventFlyerUpload:
//   • Works BEFORE an event ID exists — the file is held in local state and
//     uploaded by the parent after the event row is created.
//   • Shows a client-side image preview (URL.createObjectURL).
//   • Validates MIME type + 5 MB size client-side so bad files are rejected
//     before we bother creating the event.

import React, { useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import { UploadSimple, Trash, ImageSquare } from '@phosphor-icons/react';

const ALLOWED_TYPES = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
const MAX_BYTES = 5 * 1024 * 1024;

export function validateFlyerFile(file) {
  if (!file) return null;
  if (!ALLOWED_TYPES.includes(file.type)) {
    return 'Please choose a JPG, PNG, or WEBP image.';
  }
  if (file.size > MAX_BYTES) {
    return 'That image is over 5 MB. Please choose a smaller file.';
  }
  return null;
}

export default function EventFlyerLocalPicker({ file, onChange, testIdPrefix = 'create-flyer' }) {
  const inputRef = useRef(null);
  const [preview, setPreview] = useState(null);

  useEffect(() => {
    if (!file) { setPreview(null); return; }
    const url = URL.createObjectURL(file);
    setPreview(url);
    return () => URL.revokeObjectURL(url);
  }, [file]);

  const pick = (f) => {
    const err = validateFlyerFile(f);
    if (err) { toast.error(err); return; }
    onChange(f);
  };

  return (
    <div className="space-y-3" data-testid={`${testIdPrefix}-wrap`}>
      {preview ? (
        <div className="relative border border-[#e3dcd1] rounded-xl overflow-hidden bg-[#f7f4ed]" data-testid={`${testIdPrefix}-preview`}>
          <img
            src={preview}
            alt="Flyer preview"
            className="block w-full h-auto max-h-[240px] object-contain mx-auto"
          />
        </div>
      ) : (
        <div
          className="border border-dashed border-[#e3dcd1] rounded-xl bg-[#faf7f0] px-4 py-6 flex flex-col items-center justify-center text-center"
          data-testid={`${testIdPrefix}-empty`}
        >
          <ImageSquare size={26} weight="duotone" className="text-[#9aa39d] mb-1" />
          <div className="text-[13px] text-[#013523] font-medium">Add a promotional flyer (optional)</div>
          <div className="text-[11.5px] text-[#6a766e] mt-0.5">JPG, PNG or WEBP · up to 5 MB</div>
        </div>
      )}

      <div className="flex flex-wrap gap-2">
        <input
          ref={inputRef}
          type="file"
          accept="image/jpeg,image/png,image/webp"
          className="sr-only"
          data-testid={`${testIdPrefix}-input`}
          onChange={(e) => {
            const f = e.target.files?.[0];
            if (f) pick(f);
            if (inputRef.current) inputRef.current.value = '';
          }}
        />
        <button
          type="button"
          onClick={() => inputRef.current?.click()}
          data-testid={`${testIdPrefix}-pick-btn`}
          className="inline-flex items-center gap-1.5 px-3 py-2 rounded-full border border-[#013523] text-[#013523] text-xs min-h-[44px]"
        >
          <UploadSimple size={14} /> {file ? 'Choose a different image' : 'Choose flyer image'}
        </button>
        {file && (
          <button
            type="button"
            onClick={() => onChange(null)}
            data-testid={`${testIdPrefix}-clear-btn`}
            className="inline-flex items-center gap-1.5 px-3 py-2 rounded-full border border-[#e3dcd1] text-[#8a1c1c] text-xs min-h-[44px]"
          >
            <Trash size={14} /> Remove
          </button>
        )}
      </div>
    </div>
  );
}
